import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { MessageCircle, Edit, Trash2 } from "lucide-react";
import { ShopMessageThread } from "@/components/ShopMessageThread";
import { useNavigate } from "react-router-dom";

export default function MyListings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchListings();
    }
  }, [user]);

  const fetchListings = async () => {
    if (!user) return;

    setLoading(true);
    
    // Get parent ID
    const { data: parentData } = await supabase
      .from('parents')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!parentData) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('shop_items')
      .select('*')
      .eq('seller_parent_id', parentData.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error loading listings",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setListings(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this listing?")) return;

    const { error } = await supabase
      .from('shop_items')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error deleting listing",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({ title: "Listing deleted successfully" });
      fetchListings();
    }
  };

  const handleMarkSold = async (id: string) => {
    const { error } = await supabase
      .from('shop_items')
      .update({ status: 'sold' })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error updating listing",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({ title: "Item marked as sold" });
      fetchListings();
    }
  };

  const getStatusBadge = (item: any) => {
    if (item.status === 'sold') {
      return <Badge variant="secondary">Sold</Badge>;
    }
    if (item.approval_status === 'pending') {
      return <Badge variant="outline">Pending Approval</Badge>;
    }
    if (item.approval_status === 'rejected') {
      return <Badge variant="destructive">Rejected</Badge>;
    }
    if (item.expires_at && new Date(item.expires_at) < new Date()) {
      return <Badge variant="secondary">Expired</Badge>;
    }
    return <Badge className="bg-green-500">Active</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">My Listings</h1>
          <p className="text-muted-foreground mt-2">Manage your marketplace items</p>
        </div>
        <Button onClick={() => navigate('/marketplace/create')}>
          Create New Listing
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Items</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading listings...</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">You haven't listed any items yet</p>
              <Button 
                onClick={() => navigate('/marketplace/create')}
                className="mt-4"
              >
                Create Your First Listing
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {listing.images && listing.images[0] ? (
                          <img 
                            src={listing.images[0]} 
                            alt={listing.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            📦
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{listing.name}</div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {listing.description}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{listing.category}</TableCell>
                    <TableCell>{getStatusBadge(listing)}</TableCell>
                    <TableCell>
                      {new Date(listing.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedItemId(listing.id)}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        {listing.approval_status === 'approved' && listing.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkSold(listing.id)}
                          >
                            Mark Sold
                          </Button>
                        )}
                        {listing.approval_status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(listing.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedItemId && user && (
        <ShopMessageThread
          itemId={selectedItemId}
          itemName="Item"
          open={!!selectedItemId}
          onOpenChange={(open) => !open && setSelectedItemId(null)}
        />
      )}
    </div>
  );
}
